import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowUpRight, Shield, Users, Wrench, Factory, Truck, Settings } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export const Home = () => {
  const { t, language } = useLanguage();

  return (
    <div data-testid="home-page" className="bg-[#0f1419]">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background - Industrial refinery at night */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1566221857770-508d35ee6220?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxvaWwlMjByZWZpbmVyeSUyMHBpcGVzJTIwaW5kdXN0cmlhbCUyMHBsYW50JTIwZGFya3xlbnwwfHx8fDE3NzMwNzQ1Mzl8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1419] via-[#0f1419]/90 to-[#0f1419]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-transparent to-[#0f1419]/30" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-48 md:pb-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-blue-400 text-sm font-mono mb-6 tracking-wider">
              INDUSTRIAL SERVICES · NORDIC REGION
            </p>
            
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-8"
              data-testid="hero-headline"
            >
              Operational Excellence for Industrial Environments
            </h1>
            
            <p 
              className="text-xl text-gray-400 max-w-2xl mb-12 leading-relaxed"
              data-testid="hero-subheadline"
            >
              Professional support for industrial operations, logistics, and technical services. 
              Delivering reliable solutions across the Nordic region.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-none px-8 h-14 text-sm font-semibold group"
                data-testid="hero-cta-services"
              >
                <Link to="/services">
                  {t('cta', translations.hero)}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-gray-600 text-white hover:bg-white/5 rounded-none px-8 h-14 text-sm font-semibold"
                data-testid="hero-cta-contact"
              >
                <Link to="/contact">
                  {t('ctaSecondary', translations.hero)}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#0a0d10]/90 glass border-t border-gray-800/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: 'Nordic', label: 'Regional Coverage' },
                { value: '500+', label: 'Projects Delivered' },
                { value: '100+', label: 'Trusted Clients' },
                { value: '24/7', label: 'Support Ready' },
              ].map((stat, i) => (
                <div key={i} className="text-center md:text-left">
                  <p className="text-xl md:text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-500 font-mono tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 lg:py-32 border-t border-gray-800/50" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeIn} className="mb-16">
            <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">WHAT WE DO</p>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight" data-testid="services-overview-title">
                Comprehensive Industrial<br />
                <span className="text-gray-500">Support Services</span>
              </h2>
              <Link 
                to="/services" 
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
              >
                View All Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Primary: Industrial & Logistics */}
            <motion.div {...fadeIn}>
              <Link 
                to="/services" 
                className="group block relative h-[500px] overflow-hidden card-hover"
                data-testid="service-card-industrial"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center img-zoom"
                  style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxmb3JrbGlmdCUyMHdhcmVob3VzZSUyMGludGVyaW9yJTIwaW5kdXN0cmlhbCUyMGxvZ2lzdGljc3xlbnwwfHx8fDE3NzMwNzQ1NDR8MA&ixlib=rb-4.1.0&q=85)',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-[#0f1419]/70 to-transparent" />
                <div className="absolute inset-0 p-8 lg:p-10 flex flex-col justify-end">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
                      <Factory className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-blue-400 text-xs font-mono tracking-wider">PRIMARY SERVICES</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                    Industrial & Logistics
                  </h3>
                  <p className="text-gray-400 mb-6 max-w-md">
                    Facility support, warehouse operations, transportation coordination, and comprehensive logistics solutions.
                  </p>
                  <div className="flex items-center gap-2 text-blue-400 font-semibold text-sm group-hover:gap-4 transition-all">
                    <span>Explore Services</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Secondary: Technical Services */}
            <motion.div {...fadeIn}>
              <Link 
                to="/services" 
                className="group block relative h-[500px] overflow-hidden card-hover"
                data-testid="service-card-technical"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center img-zoom"
                  style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1573153178631-49e3aa9e018b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxvaWwlMjByZWZpbmVyeSUyMHBpcGVzJTIwaW5kdXN0cmlhbCUyMHBsYW50JTIwZGFya3xlbnwwfHx8fDE3NzMwNzQ1Mzl8MA&ixlib=rb-4.1.0&q=85)',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-[#0f1419]/70 to-transparent" />
                <div className="absolute inset-0 p-8 lg:p-10 flex flex-col justify-end">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gray-700 flex items-center justify-center">
                      <Settings className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-gray-400 text-xs font-mono tracking-wider">TECHNICAL EXPERTISE</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                    Technical Services
                  </h3>
                  <p className="text-gray-400 mb-6 max-w-md">
                    Equipment maintenance, repair services, inspections, HVAC systems, and specialized technical support.
                  </p>
                  <div className="flex items-center gap-2 text-blue-400 font-semibold text-sm group-hover:gap-4 transition-all">
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 lg:py-32 bg-[#0a0d10] border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn}>
              <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">OUR EXPERTISE</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-8 leading-tight">
                Professional Support Across<br />
                <span className="text-gray-500">The Nordic Region</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  'Industrial facility support',
                  'Logistics & warehouse operations',
                  'Equipment maintenance',
                  'Technical repair services',
                  'Transportation coordination',
                  'Specialized technical support'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                    <span className="text-gray-400 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div {...fadeIn} className="relative">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1768796373708-e1b62a0f2900?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxmb3JrbGlmdCUyMHdhcmVob3VzZSUyMGludGVyaW9yJTIwaW5kdXN0cmlhbCUyMGxvZ2lzdGljc3xlbnwwfHx8fDE3NzMwNzQ1NDR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Warehouse operations"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 lg:py-32 border-t border-gray-800/50" data-testid="value-props-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-16">
            <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">WHY NATCAS GROUP</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              Professional Experience & Technical Expertise
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: t('title', translations.values.reliability),
                description: t('description', translations.values.reliability),
              },
              {
                icon: Users,
                title: t('title', translations.values.expertise),
                description: t('description', translations.values.expertise),
              },
              {
                icon: Wrench,
                title: t('title', translations.values.support),
                description: t('description', translations.values.support),
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                {...fadeIn}
                className="group p-8 bg-[#0a0d10] border border-gray-800 hover:border-blue-600/50 transition-colors"
                data-testid={`value-prop-${i}`}
              >
                <div className="w-14 h-14 bg-gray-800 group-hover:bg-blue-600 transition-colors flex items-center justify-center mb-6">
                  <item.icon className="w-6 h-6 text-blue-400 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-24 lg:py-32 overflow-hidden border-t border-gray-800/50"
        data-testid="cta-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1769144256181-698b8f807066?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxjb250YWluZXIlMjBwb3J0JTIwY3JhbmVzJTIwbG9naXN0aWNzJTIwYWVyaWFsJTIwdmlld3xlbnwwfHx8fDE3NzMwNzQ1MTJ8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-[#0f1419]/90" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeIn}>
            <p className="text-blue-400 text-sm font-mono mb-6 tracking-wider">START YOUR PROJECT</p>
            <h2 
              className="text-4xl lg:text-5xl font-bold text-white mb-10"
              data-testid="cta-title"
            >
              {t('title', translations.cta)}
            </h2>
            <Button
              asChild
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-none px-10 h-14 text-sm font-semibold group"
              data-testid="cta-button"
            >
              <Link to="/contact">
                {t('button', translations.cta)}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
